'use client';

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { ArrowDefaultIcon, ArrowChevronIcon } from '../../../icons';

// ============================================
// useIsMobile Hook (Internal)
// ============================================
const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// ============================================
// Context
// ============================================
type PaginationTheme = 'light' | 'dark';

type PaginationContextValue = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  responsive?: boolean;
  isMobile?: boolean;
  theme?: PaginationTheme;
};

const PaginationContext = createContext<PaginationContextValue | null>(null);

const usePaginationContext = () => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error('Pagination components must be used within Pagination');
  }
  return context;
};

// ============================================
// Pagination.First
// ============================================
type PaginationFirstProps = {
  className?: string;
  children?: React.ReactNode;
};

const PaginationFirst: React.FC<PaginationFirstProps> = ({
  className,
  children,
}) => {
  const { currentPage, onPageChange, disabled, responsive, theme } =
    usePaginationContext();
  const isDark = theme === 'dark';
  const isDisabled = disabled || currentPage <= 1;

  return (
    <button
      type="button"
      onClick={() => onPageChange(1)}
      disabled={isDisabled}
      className={cn(
        'flex items-center justify-center rounded-md',
        responsive ? 'w-7 h-7 md:w-8 md:h-8' : 'w-8 h-8',
        isDark
          ? 'text-gray-400 hover:text-white hover:bg-white/[0.08]'
          : 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-800',
        'transition-colors duration-150',
        isDisabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
        className,
      )}
    >
      {children ?? (
        <span className="flex">
          <ArrowChevronIcon
            className={cn(
              responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
              '-mr-2 rotate-90',
            )}
          />
          <ArrowChevronIcon
            className={cn(
              responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
              'rotate-90',
            )}
          />
        </span>
      )}
    </button>
  );
};

PaginationFirst.displayName = 'Pagination.First';

// ============================================
// Pagination.Prev
// ============================================
type PaginationPrevProps = {
  className?: string;
  children?: React.ReactNode;
};

const PaginationPrev: React.FC<PaginationPrevProps> = ({
  className,
  children,
}) => {
  const { currentPage, onPageChange, disabled, responsive, theme } =
    usePaginationContext();
  const isDark = theme === 'dark';
  const isDisabled = disabled || currentPage <= 1;

  return (
    <button
      type="button"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={isDisabled}
      className={cn(
        'flex items-center justify-center rounded-md',
        responsive ? 'w-7 h-7 md:w-8 md:h-8' : 'w-8 h-8',
        isDark
          ? 'text-gray-400 hover:text-white hover:bg-white/[0.08]'
          : 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-800',
        'transition-colors duration-150',
        isDisabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
        className,
      )}
    >
      {children ?? (
        <ArrowChevronIcon
          className={cn(
            responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
            'rotate-90',
          )}
        />
      )}
    </button>
  );
};

PaginationPrev.displayName = 'Pagination.Prev';

// ============================================
// Pagination.Next
// ============================================
type PaginationNextProps = {
  className?: string;
  children?: React.ReactNode;
};

const PaginationNext: React.FC<PaginationNextProps> = ({
  className,
  children,
}) => {
  const { currentPage, totalPages, onPageChange, disabled, responsive, theme } =
    usePaginationContext();
  const isDark = theme === 'dark';
  const isDisabled = disabled || currentPage >= totalPages;

  return (
    <button
      type="button"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={isDisabled}
      className={cn(
        'flex items-center justify-center rounded-md',
        responsive ? 'w-7 h-7 md:w-8 md:h-8' : 'w-8 h-8',
        isDark
          ? 'text-gray-400 hover:text-white hover:bg-white/[0.08]'
          : 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-800',
        'transition-colors duration-150',
        isDisabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
        className,
      )}
    >
      {children ?? (
        <ArrowChevronIcon
          className={cn(
            responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
            '-rotate-90',
          )}
        />
      )}
    </button>
  );
};

PaginationNext.displayName = 'Pagination.Next';

// ============================================
// Pagination.Last
// ============================================
type PaginationLastProps = {
  className?: string;
  children?: React.ReactNode;
};

const PaginationLast: React.FC<PaginationLastProps> = ({
  className,
  children,
}) => {
  const { currentPage, totalPages, onPageChange, disabled, responsive, theme } =
    usePaginationContext();
  const isDark = theme === 'dark';
  const isDisabled = disabled || currentPage >= totalPages;

  return (
    <button
      type="button"
      onClick={() => onPageChange(totalPages)}
      disabled={isDisabled}
      className={cn(
        'flex items-center justify-center rounded-md',
        responsive ? 'w-7 h-7 md:w-8 md:h-8' : 'w-8 h-8',
        isDark
          ? 'text-gray-400 hover:text-white hover:bg-white/[0.08]'
          : 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-800',
        'transition-colors duration-150',
        isDisabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
        className,
      )}
    >
      {children ?? (
        <span className="flex">
          <ArrowChevronIcon
            className={cn(
              responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
              '-mr-2 -rotate-90',
            )}
          />
          <ArrowChevronIcon
            className={cn(
              responsive ? 'w-3.5 h-3.5 md:w-4 md:h-4' : 'w-4 h-4',
              '-rotate-90',
            )}
          />
        </span>
      )}
    </button>
  );
};

PaginationLast.displayName = 'Pagination.Last';

// ============================================
// Pagination.Pages
// ============================================
type PaginationPagesProps = {
  maxVisible?: number;
  mobileMaxVisible?: number;
  className?: string;
};

const PaginationPages: React.FC<PaginationPagesProps> = ({
  maxVisible = 7,
  mobileMaxVisible = 5,
  className,
}) => {
  const {
    currentPage,
    totalPages,
    onPageChange,
    disabled,
    responsive,
    isMobile,
    theme,
  } = usePaginationContext();
  const isDark = theme === 'dark';

  const effectiveMaxVisible =
    responsive && isMobile ? mobileMaxVisible : maxVisible;

  const pages = useMemo(() => {
    const result: (number | 'ellipsis')[] = [];

    if (totalPages <= effectiveMaxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        result.push(i);
      }
      return result;
    }

    // First and last page are always shown; `siblingCount` pages sit on each
    // side of the current page, forming a window of `windowSize` numbers.
    const boundaryCount = 1;
    const siblingCount = Math.max(1, Math.floor((effectiveMaxVisible - 3) / 2));
    const windowSize = siblingCount * 2 + 1;

    let leftSibling = Math.max(currentPage - siblingCount, boundaryCount + 1);
    let rightSibling = Math.min(
      currentPage + siblingCount,
      totalPages - boundaryCount,
    );

    // Near the edges the window is clamped on one side, so borrow the unused
    // slots from the other side to keep a constant number of page buttons.
    if (rightSibling - leftSibling + 1 < windowSize) {
      if (leftSibling === boundaryCount + 1) {
        rightSibling = Math.min(
          leftSibling + windowSize - 1,
          totalPages - boundaryCount,
        );
      } else if (rightSibling === totalPages - boundaryCount) {
        leftSibling = Math.max(rightSibling - windowSize + 1, boundaryCount + 1);
      }
    }

    result.push(1);

    // Left gap: a single hidden page is shown directly instead of an ellipsis.
    if (leftSibling > boundaryCount + 2) {
      result.push('ellipsis');
    } else if (leftSibling === boundaryCount + 2) {
      result.push(boundaryCount + 1);
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
      result.push(i);
    }

    // Right gap: symmetric to the left gap.
    if (rightSibling < totalPages - boundaryCount - 1) {
      result.push('ellipsis');
    } else if (rightSibling === totalPages - boundaryCount - 1) {
      result.push(totalPages - boundaryCount);
    }

    result.push(totalPages);

    return result;
  }, [currentPage, totalPages, effectiveMaxVisible]);

  return (
    <div className={cn('flex items-center gap-0.5 md:gap-1', className)}>
      {pages.map((page, index) =>
        page === 'ellipsis' ? (
          <span
            key={`ellipsis-${index}`}
            className={cn(
              'flex items-center justify-center',
              isDark ? 'text-gray-400' : 'text-gscale-400',
              responsive
                ? 'w-7 h-7 md:w-8 md:h-8 text-xs md:text-sm'
                : 'w-8 h-8',
            )}
          >
            ...
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            disabled={disabled}
            className={cn(
              'flex items-center justify-center rounded-md',
              responsive
                ? 'w-7 h-7 md:w-8 md:h-8 text-xs md:text-sm'
                : 'w-8 h-8 text-sm',
              'transition-colors duration-150',
              page === currentPage
                ? isDark
                  ? 'text-white font-medium'
                  : 'bg-gscale-100 text-gscale-800 font-medium'
                : isDark
                  ? 'text-gray-400 hover:text-white hover:bg-white/[0.08]'
                  : 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-800',
              disabled && 'opacity-40 cursor-not-allowed',
            )}
          >
            {page}
          </button>
        ),
      )}
    </div>
  );
};

PaginationPages.displayName = 'Pagination.Pages';

// ============================================
// Pagination.Info
// ============================================
type PaginationInfoProps = {
  totalItems?: number;
  itemsPerPage?: number;
  className?: string;
  format?: (start: number, end: number, total: number) => string;
};

const PaginationInfo: React.FC<PaginationInfoProps> = ({
  totalItems,
  itemsPerPage = 10,
  className,
  format,
}) => {
  const { currentPage, theme } = usePaginationContext();
  const isDark = theme === 'dark';

  if (!totalItems) return null;

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);

  const text = format
    ? format(start, end, totalItems)
    : `${start}-${end} / ${totalItems}`;

  return (
    <span className={cn('text-sm', isDark ? 'text-gray-400' : 'text-gscale-500', className)}>{text}</span>
  );
};

PaginationInfo.displayName = 'Pagination.Info';

// ============================================
// Pagination.GoTo
// ============================================
type PaginationGoToProps = {
  className?: string;
  placeholder?: string;
  buttonText?: string;
  showButton?: boolean;
};

const PaginationGoTo: React.FC<PaginationGoToProps> = ({
  className,
  placeholder = '페이지',
  buttonText = '이동',
  showButton = true,
}) => {
  const { currentPage, totalPages, onPageChange, disabled, responsive } =
    usePaginationContext();
  const [inputValue, setInputValue] = useState(String(currentPage));

  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const page = parseInt(inputValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      onPageChange(page);
    } else {
      setInputValue(String(currentPage));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div
      className={cn(
        'flex items-center',
        'border border-gscale-200 rounded-md overflow-hidden',
        'focus-within:ring-1 focus-within:ring-gscale-400 focus-within:border-gscale-400',
        'transition-colors duration-150',
        disabled && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={inputValue}
        onChange={e => {
          const v = e.target.value;
          if (v === '' || /^\d+$/.test(v)) setInputValue(v);
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'border-none text-center bg-transparent',
          'focus:outline-none',
          'text-gscale-700',
          'px-[0.25rem]',
          responsive
            ? 'w-8 h-7 text-xs md:w-10 md:h-8 md:text-sm'
            : 'w-10 h-8 text-sm',
        )}
      />
      {showButton && (
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={disabled || !inputValue}
          className={cn(
            'flex items-center justify-center',
            'border-l border-gscale-200 bg-gscale-50 text-gscale-500',
            'hover:bg-gscale-100 hover:text-gscale-700',
            'transition-colors duration-150',
            responsive ? 'w-7 h-7 md:w-8 md:h-8' : 'w-8 h-8',
            (disabled || !inputValue) &&
              'cursor-not-allowed hover:bg-gscale-50 hover:text-gscale-500',
          )}
        >
          <ArrowDefaultIcon
            className={cn(
              responsive ? 'w-3 h-3 md:w-3.5 md:h-3.5' : 'w-3.5 h-3.5',
              'rotate-180',
            )}
          />
        </button>
      )}
    </div>
  );
};

PaginationGoTo.displayName = 'Pagination.GoTo';

// ============================================
// Pagination (Main Component)
// ============================================
const paginationVariants = cva('flex items-center', {
  variants: {
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    gap: {
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
    },
  },
  defaultVariants: {
    justify: 'center',
    gap: 'sm',
  },
});

interface PaginationProps extends VariantProps<typeof paginationVariants> {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  // Simple mode props
  maxVisible?: number;
  mobileMaxVisible?: number;
  showFirstLast?: boolean;
  responsive?: boolean;
  theme?: PaginationTheme;
}

const PaginationRoot: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  children,
  className,
  justify,
  gap,
  // Simple mode
  maxVisible = 7,
  mobileMaxVisible = 5,
  showFirstLast = true,
  responsive = true,
  theme = 'light',
}) => {
  const isMobile = useIsMobile();

  const contextValue = useMemo(
    () => ({
      currentPage,
      totalPages,
      onPageChange,
      disabled,
      responsive,
      isMobile,
      theme,
    }),
    [currentPage, totalPages, onPageChange, disabled, responsive, isMobile, theme],
  );

  // Simple mode (no children)
  if (!children) {
    return (
      <PaginationContext.Provider value={contextValue}>
        <div className={cn(paginationVariants({ justify, gap }), className)}>
          {showFirstLast && <PaginationFirst />}
          <PaginationPrev />
          <PaginationPages
            maxVisible={maxVisible}
            mobileMaxVisible={mobileMaxVisible}
          />
          <PaginationNext />
          {showFirstLast && <PaginationLast />}
        </div>
      </PaginationContext.Provider>
    );
  }

  // Compound mode (with children)
  return (
    <PaginationContext.Provider value={contextValue}>
      <div className={cn(paginationVariants({ justify, gap }), className)}>
        {children}
      </div>
    </PaginationContext.Provider>
  );
};

PaginationRoot.displayName = 'Pagination';

// ============================================
// Compound Component Export
// ============================================
export const Pagination = Object.assign(PaginationRoot, {
  First: PaginationFirst,
  Prev: PaginationPrev,
  Pages: PaginationPages,
  Next: PaginationNext,
  Last: PaginationLast,
  Info: PaginationInfo,
  GoTo: PaginationGoTo,
});

export type {
  PaginationProps,
  PaginationFirstProps,
  PaginationPrevProps,
  PaginationPagesProps,
  PaginationNextProps,
  PaginationLastProps,
  PaginationInfoProps,
  PaginationGoToProps,
};
